import React from "react";
import { Row, Col, Card, Button, Badge, Space, Tag } from "antd";
import { PrinterOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined, CreditCardOutlined, FileTextOutlined, WarningOutlined, CheckCircleOutlined } from "@ant-design/icons";
import Swiggy from "../../assets/images/swiggy1.svg";
import Zomato from "../../assets/images/zomato.svg";
import SwiggyBolt from "../../assets/images/swiggyBolt.svg";
import WhatServe from "../../assets/images/whatServe.jpeg";
import { isItemOutOfStock, isItemPartialStock } from "../../utils/aggregatorStockCheck";
import { getShipmentStatusDisplay, getOrderInvoiceStatusDisplay } from "../../utils/aggregatorFulfilmentStatus";
import useAuthStore from "../../stores/useAuthStore";
import { formatCurrencyAmount } from "../../utils/tillHelpers";

const OrderDetails = ({
  selectedOrder,
  statusDisplayMapping,
  onPrint,
  actionButtons,
  stockMap = {},
  shipmentMode,
  shipmentStatus,
  orderInvoiceStatus,
  showOutOfStockBanner = false,
  outOfStockBeforeAccept = false,
  outOfStockCount = 0,
}) => {
  const channelIcons = { swiggy: Swiggy, zomato: Zomato, swiggybolt: SwiggyBolt, whatsserve: WhatServe };
  const tillData = useAuthStore((s) => s.tillData);

  const formatCurrency = (amount) => formatCurrencyAmount(tillData, amount);
  const formatTime = (timeString) => new Date(timeString).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  const statusConfig = {
    NEW: { color: "#4F23F1", label: statusDisplayMapping["NEW"] || "New" },
    AK: { color: "#E99013", label: statusDisplayMapping["AK"] || "Acknowledged" },
    FR: { color: "#008000", label: statusDisplayMapping["FR"] || "Food Ready" },
    DI: { color: "#4F23F1", label: statusDisplayMapping["DI"] || "Dispatched" },
    CC: { color: "#dc2626", label: statusDisplayMapping["CC"] || "Cancelled" },
    CO: { color: "#07f307", label: statusDisplayMapping["CO"] || "Completed" },
    default: { color: "#4F23F1", label: statusDisplayMapping["default"] || "Unknown" },
  };

  // s_order.shipmentstatus / invoicestatus; both null until the order is fulfilled.
  const fulfilmentTags = [
    shipmentStatus && { prefix: "Shipment", ...getShipmentStatusDisplay(shipmentStatus) },
    orderInvoiceStatus && { prefix: "Invoice", ...getOrderInvoiceStatusDisplay(orderInvoiceStatus) },
  ].filter(Boolean);

  if (!selectedOrder) {
    return (
      <Card
        style={{
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          height: "calc(100vh - 120px)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#6b7280",
            fontSize: "12px",
          }}
        >
          Click on any order to view details
        </div>
      </Card>
    );
  }

  // TSK-4875 (wireframe 11a/11c): billing state beside the status tag, onBill
  // mode only. "Billed ✓" stays once billed; "Not Billed" only while the
  // order can still be billed (AK/FR).
  const isBilled = selectedOrder.isBilled === "Y";
  const billingTag =
    shipmentMode !== "onBill"
      ? null
      : isBilled
        ? { label: "Billed ✓", color: "#1F8A4C" }
        : ["AK", "FR"].includes(selectedOrder.status)
          ? { label: "Not Billed", color: "#6b7280" }
          : null;
  // TSK-4875 (wireframe 11c): once billed every line has shipped -- "Shipped"
  // replaces the stock tag, which no longer means anything for this order.
  const showShipped = shipmentMode === "onBill" && isBilled;

  const isInstantOrder = selectedOrder?.metaData?.find((md) => md.key === "Instant Order")?.value === "true";
  const channelIcon = channelIcons[selectedOrder.channel.toLowerCase()] || (isInstantOrder ? SwiggyBolt : selectedOrder.channel.toLowerCase() === "swiggy" ? Swiggy : Zomato);

  return (
    <Card
      style={{
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        height: "100%",
      }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Order Details</span>
          </Col>
          <Col>
            <Button icon={<PrinterOutlined />} onClick={onPrint} style={{ height: "28px", width: "28px", borderRadius: "6px" }} />
          </Col>
        </Row>
      }
    >
      <div style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto", padding: "0 12px 12px" }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {/* Order Header */}
          <Row justify="space-between" align="middle">
            <Col>
              <Row align="middle" gutter={8}>
                <Col>
                  <div style={{ fontSize: "16px", fontWeight: 600 }}>{selectedOrder.orderNo}</div>
                  <div style={{ fontSize: "10px", color: "#6b7280" }}>{formatTime(selectedOrder.orderTime)}</div>
                </Col>
              </Row>
            </Col>
            <Col>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "14px", fontWeight: 600 }}>{formatCurrency(selectedOrder.amount)}</div>
                <Tag
                  style={{
                    backgroundColor: `${statusConfig[selectedOrder.status]?.color || statusConfig.default.color}20`,
                    color: statusConfig[selectedOrder.status]?.color || statusConfig.default.color,
                    border: `1px solid ${statusConfig[selectedOrder.status]?.color || statusConfig.default.color}40`,
                    fontSize: "10px",
                    padding: "2px 6px",
                    fontWeight: "bold",
                    borderRadius: "5px",
                  }}
                >
                  {statusConfig[selectedOrder.status]?.label || statusConfig.default.label}
                </Tag>
                {billingTag && (
                  <Tag
                    style={{
                      marginLeft: "4px",
                      backgroundColor: `${billingTag.color}20`,
                      color: billingTag.color,
                      border: `1px solid ${billingTag.color}40`,
                      fontSize: "10px",
                      padding: "2px 6px",
                      fontWeight: "bold",
                      borderRadius: "5px",
                    }}
                  >
                    {billingTag.label}
                  </Tag>
                )}
                {fulfilmentTags.length > 0 && (
                  <div style={{ marginTop: "4px" }}>
                    {fulfilmentTags.map((tag) => (
                      <Tag
                        key={tag.prefix}
                        style={{
                          marginLeft: "4px",
                          marginRight: 0,
                          backgroundColor: `${tag.color}20`,
                          color: tag.color,
                          border: `1px solid ${tag.color}40`,
                          fontSize: "10px",
                          padding: "2px 6px",
                          fontWeight: "bold",
                          borderRadius: "5px",
                        }}
                      >
                        {tag.prefix}: {tag.label}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Customer Information */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Customer Information</div>
            <Space direction="vertical" size={4} style={{ fontSize: "12px" }}>
              <Row align="middle" gutter={8}>
                <Col>
                  <UserOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col>{selectedOrder.customerName || "N/A"}</Col>
              </Row>
              <Row align="middle" gutter={8}>
                <Col>
                  <PhoneOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col style={{ color: "#6b7280", fontSize: "10px" }}>{selectedOrder.customerPhone || "N/A"}</Col>
              </Row>
              <Row align="middle" gutter={8}>
                <Col>
                  <EnvironmentOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col style={{ color: "#6b7280", fontSize: "10px" }}>{selectedOrder.customerAddress || "N/A"}</Col>
              </Row>
            </Space>
          </div>

          {/* Order Items */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Order Items</div>
            {/* TSK-4875: unbilled onBill order (wireframe 11b) or NEW onAccept order
                (wireframe 11d) with out-of-stock lines. */}
            {/* TSK-4875 (wireframe 11c): billed banner. Bill time / user / shipment
                and invoice numbers are not on the order yet (backend sends isBilled
                only), so this is the wireframe's bar without those details. */}
            {showShipped && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  marginBottom: "6px",
                  padding: "6px 8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#1F8A4C",
                  background: "#DCFCE7",
                  border: "1px solid rgba(31,138,76,0.25)",
                  borderRadius: "6px",
                }}
              >
                <CheckCircleOutlined style={{ fontSize: "12px", marginTop: "2px" }} />
                <span>Bill generated</span>
              </div>
            )}
            {showOutOfStockBanner && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  marginBottom: "6px",
                  padding: "6px 8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  // Wireframe 11d is red (accepting will ship the OOS item); 11b is amber.
                  color: outOfStockBeforeAccept ? "#B91C1C" : "#B45309",
                  background: outOfStockBeforeAccept ? "#FEF2F2" : "#FEF3C7",
                  border: `1px solid ${outOfStockBeforeAccept ? "rgba(220,38,38,0.25)" : "rgba(180,83,9,0.25)"}`,
                  borderRadius: "6px",
                }}
              >
                <WarningOutlined style={{ fontSize: "12px", marginTop: "2px" }} />
                {outOfStockBeforeAccept ? (
                  <span>
                    {outOfStockCount} {outOfStockCount === 1 ? "item" : "items"} out of stock — modify before accepting (accept creates shipment immediately)
                  </span>
                ) : (
                  <span>
                    {outOfStockCount} of {selectedOrder.items.length} {selectedOrder.items.length === 1 ? "item" : "items"} out of stock — modify items before generating bill
                  </span>
                )}
              </div>
            )}
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {selectedOrder.items.map((item, index) => (
                <Row key={index} justify="space-between" align="middle" style={{ fontSize: "12px" }}>
                  <Col>
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                    <span style={{ color: "#6b7280", marginLeft: "8px" }}>x{item.quantity}</span>
                    {showShipped ? (
                      <Tag
                        style={{
                          marginLeft: "8px",
                          fontSize: "8px",
                          fontWeight: 700,
                          padding: "0 5px",
                          background: "#F3F4F6",
                          color: "#6b7280",
                          border: "1px solid rgba(107,114,128,0.2)",
                        }}
                      >
                        Shipped
                      </Tag>
                    ) : isItemOutOfStock(item, stockMap) ? (
                      <Tag
                        style={{
                          marginLeft: "8px",
                          fontSize: "8px",
                          fontWeight: 700,
                          padding: "0 5px",
                          background: "#FEE2E2",
                          color: "#DC2626",
                          border: "1px solid rgba(220,38,38,0.2)",
                        }}
                      >
                        Out of Stock
                      </Tag>
                    ) : isItemPartialStock(item, stockMap) ? (
                      // TSK-4127: distinct from "Out of Stock" — some of the ordered
                      // qty is available, so the operator can partially fulfil this
                      // line via Edit Qty instead of replacing it outright.
                      <Tag
                        style={{
                          marginLeft: "8px",
                          fontSize: "8px",
                          fontWeight: 700,
                          padding: "0 5px",
                          background: "#FEF3C7",
                          color: "#B45309",
                          border: "1px solid rgba(180,83,9,0.2)",
                        }}
                      >
                        Partial Stock
                      </Tag>
                    ) : stockMap[item.sOrderlineID] ? (
                      <Tag
                        style={{
                          marginLeft: "8px",
                          fontSize: "8px",
                          fontWeight: 700,
                          padding: "0 5px",
                          background: "#DCFCE7",
                          color: "#16A34A",
                          border: "1px solid rgba(22,163,74,0.2)",
                        }}
                      >
                        In Stock
                      </Tag>
                    ) : null}
                    {item.addons?.length > 0 && (
                      <div style={{ marginLeft: "16px", color: "#6b7280", fontSize: "10px" }}>
                        {item.addons.map((addon, idx) => (
                          <div key={idx}>
                            - {addon.name} (Qty: {addon.qty}, {formatCurrency(addon.price)})
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>
                  <Col>{formatCurrency(item.price * item.quantity)}</Col>
                </Row>
              ))}
            </Space>
          </div>

          {/* Order Summary */}
          <div style={{ borderTop: "1px solid #d1d5db", paddingTop: "8px" }}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Row justify="space-between" style={{ fontSize: "12px" }}>
                <Col>Subtotal</Col>
                <Col>{formatCurrency(selectedOrder.amount - selectedOrder.tax)}</Col>
              </Row>
              <Row justify="space-between" style={{ fontSize: "12px" }}>
                <Col>Tax</Col>
                <Col>{formatCurrency(selectedOrder.tax)}</Col>
              </Row>
              {selectedOrder.discount > 0 && (
                <Row justify="space-between" style={{ fontSize: "12px", color: "#16a34a" }}>
                  <Col>Discount</Col>
                  <Col>-{formatCurrency(selectedOrder.discount)}</Col>
                </Row>
              )}
              <Row justify="space-between" style={{ fontSize: "12px", fontWeight: 600, borderTop: "1px solid #d1d5db", paddingTop: "4px" }}>
                <Col>Total</Col>
                <Col>{formatCurrency(selectedOrder.amount)}</Col>
              </Row>
            </Space>
          </div>

          {/* Additional Info */}
          <div>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Row align="middle" gutter={8} style={{ fontSize: "12px" }}>
                <Col>
                  <CreditCardOutlined style={{ fontSize: "12px" }} />
                </Col>
                <Col>{selectedOrder.paymentMode || "N/A"}</Col>
              </Row>
              <Row align="middle" gutter={8} style={{ fontSize: "12px" }}>
                <Col>
                  <img src={channelIcon} style={{ height: "20px" }} alt={selectedOrder.channel} />
                </Col>
                <Col>{selectedOrder.channel}</Col>
              </Row>
              {selectedOrder.metaData?.find((md) => md.key === "Instructions") && (
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Order Instructions</div>
                  <Row align="middle" gutter={8} style={{ fontSize: "10px", color: "#6b7280" }}>
                    <Col>
                      <FileTextOutlined style={{ fontSize: "12px" }} />
                    </Col>
                    <Col>{selectedOrder.metaData.find((md) => md.key === "Instructions")?.value || "N/A"}</Col>
                  </Row>
                </div>
              )}
              {selectedOrder.metaData?.find((md) => md.key === "Charges") && (
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Packaging Charges</div>
                  {(() => {
                    try {
                      const chargesData = selectedOrder.metaData.find((md) => md.key === "Charges");
                      const parsedCharges = JSON.parse(chargesData.value);
                      return parsedCharges.map((charge, index) => (
                        <Row key={index} justify="space-between" style={{ fontSize: "10px" }}>
                          <Col>{charge.title}</Col>
                          <Col>{formatCurrency(charge.value)}</Col>
                        </Row>
                      ));
                    } catch (e) {
                      console.error("Failed to parse charges metadata:", e);
                      return null;
                    }
                  })()}
                </div>
              )}
              {selectedOrder.metaData?.find((md) => md.key === "Rider Status") && (
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>Delivery Info</div>
                  {(() => {
                    try {
                      const riderData = selectedOrder.metaData.find((md) => md.key === "Rider Status");
                      const parsedRider = JSON.parse(riderData.value);
                      const riderDetails = parsedRider?.delivery_info?.delivery_person_details;
                      return (
                        <Space direction="vertical" size={4}>
                          {riderDetails?.name && riderDetails.name !== "Unknown" && (
                            <Row justify="space-between" style={{ fontSize: "10px" }}>
                              <Col>Rider Name</Col>
                              <Col>{riderDetails.name}</Col>
                            </Row>
                          )}
                          {riderDetails?.phone && riderDetails.phone !== "N/A" && (
                            <Row justify="space-between" style={{ fontSize: "10px" }}>
                              <Col>Rider Phone No</Col>
                              <Col>{riderDetails.phone}</Col>
                            </Row>
                          )}
                          {parsedRider?.delivery_info?.current_state && (
                            <Row justify="space-between" style={{ fontSize: "10px" }}>
                              <Col>Rider Status</Col>
                              <Col>
                                <Badge style={{ padding: "2px 6px" }} text={parsedRider.delivery_info.current_state} />
                              </Col>
                            </Row>
                          )}
                        </Space>
                      );
                    } catch (e) {
                      console.error("Failed to parse rider metadata:", e);
                      return null;
                    }
                  })()}
                </div>
              )}
            </Space>
          </div>

          {/* Action Buttons */}
          <div style={{ borderTop: "1px solid #d1d5db", paddingTop: "8px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Quick Actions</div>
            <Row gutter={[8, 8]}>{actionButtons}</Row>
          </div>
        </Space>
      </div>
      <style jsx>{`
        .ant-card {
          border-radius: 8px !important;
        }
        .ant-card-head {
          padding: 8px 12px !important;
          border-bottom: 1px solid #d1d5db !important;
        }
        .ant-card-body {
          padding: 0 !important;
        }
        .ant-tag {
          margin-right: 0 !important;
        }
      `}</style>
    </Card>
  );
};

export default OrderDetails;
